import { APITypesV1 } from "@cur8/api-client";
import { Visit } from "@cur8/rich-entity";

/**
 * Read scan state from the visit data for a given scan type (e.g. "skin", "cardio", "tci")
 * @returns ImmutableScanState (or undefined if the scan type is not found in the visit's immutableScansPerformed object)
 */

type ScanType = "skin" | "cardio" | "tci" | "ecg";

// Worst first, so a type with several attempts and nothing finished reports the
// state worth acting on rather than whichever attempt came back first.
const SEVERITY = [
  APITypesV1.ImmutableScanState.Failed,
  APITypesV1.ImmutableScanState.Inprogress,
  APITypesV1.ImmutableScanState.Available,
];

export function readScanStateFromVisit(
  scanType: ScanType,
  visit?: Visit
): APITypesV1.ImmutableScanState | undefined {
  if (visit?.immutableScansPerformed == null) {
    return undefined;
  }

  // Prefixed, not contained: "ecg-cardiosecur" contains "cardio", so a
  // contains-match handed the ECG's state back for "cardio" whenever that key
  // came first, reporting a failed cardio scan as completed. Every id the
  // backend emits leads with its own type, "skin/1", "cardio/3" and the two
  // EcgType values "ecg-cardiosecur" and "ecg-norav".
  const states = Object.entries(visit.immutableScansPerformed)
    .filter(([key]) => key.startsWith(scanType))
    .map(([, scan]) => scan?.resultState);

  if (states.length === 0) {
    return undefined;
  }

  // A retried scan appears once per attempt ("cardio/1" and "cardio/3"), and
  // reading only the first one made the answer depend on the order the backend
  // serialised them in. One finished attempt means a usable recording exists,
  // so the exam happened however many attempts it took; a later failure is a
  // redundant extra recording, not lost data.
  if (states.includes(APITypesV1.ImmutableScanState.Finished)) {
    return APITypesV1.ImmutableScanState.Finished;
  }

  return SEVERITY.find((state) => states.includes(state)) ?? states[0];
}
